import React, { Component } from "react";
import CommonAccountPanelView from "presentational/account/CommonAccountPanelView";
import LoadingModalView from "presentational/common/modals/LoadingModalView";
import TXT from "presentational/common/multinational/TXT";
import { withStore } from "store/storeHandler";
import {
  checkEmailExist,
  deleteUnlinkedCognitoUser
} from "lib/accountDuplicationChecker";
import { isValidInput } from "lib/textFilter";
import Service from "lib/service-amp";
import PropTypes from "prop-types";

class AccountRegisterPanel extends Component {
  static propTypes = {
    onCognitoSignUpComplete: PropTypes.func.isRequired,
    onModeToggleBtnClick: PropTypes.func.isRequired,
    goToSocialPage: PropTypes.func.isRequired
  };

  state = {
    email: "",
    passwordToCreate: "",
    confirmPassword: "",
    signUpAgreement: false,
    isLoading: false
  };

  async clearUnconfirmedUser(email) {
    const { AppErrorActions } = this.props;

    try {
      const { message } = await deleteUnlinkedCognitoUser(email.trim());
      if (message === "AvailableEmail") {
        return { complete: true };
      } else if (message === "AlreadyExist") {
        this.displayNotice("emailAlreadyRegistered");
        return { complete: false };
      }
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
      this.displayNotice("defaultSignUpError");
      return { complete: false };
    }
  }

  isSignUpBtnDisabled = () => {
    const { email, passwordToCreate, confirmPassword, signUpAgreement } =
      this.state;

    if (email.length === 0) return true;
    if (!isValidInput("password", passwordToCreate.trim())) return true;
    if (confirmPassword.length === 0) return true;
    if (!signUpAgreement) return true;

    return false;
  };

  handleChange = (type, value) => {
    this.setState({ [type]: value });
  };

  handleCognitoSignUp = async () => {
    const { onCognitoSignUpComplete, goToSocialPage, AppErrorActions } =
      this.props;
    const { email, passwordToCreate, confirmPassword } = this.state;

    if (this.isSignUpBtnDisabled()) {
      return null;
    }

    if (!email) {
      this.displayNotice("emailNotFilledOut");
      return null;
    } else if (!passwordToCreate || !confirmPassword) {
      this.displayNotice("pwNotFilledOut");
      return null;
    } else if (passwordToCreate !== confirmPassword) {
      this.displayNotice("twoPwNotSame");
      return null;
    } else if (!isValidInput("email", email)) {
      this.displayNotice("wrongEmail");
      return null;
    } else if (!isValidInput("password", passwordToCreate.trim())) {
      this.displayNotice("incorrectPwFormat");
      return null;
    }

    this.setState({ isLoading: true });

    const { emailExist, isGoogleAuthUser } = await checkEmailExist(
      email.trim()
    );
    if (emailExist) {
      if (isGoogleAuthUser) {
        this.displayNotice("emailAlreadyRegisteredInGoogle", () => {
          goToSocialPage();
        });
      } else {
        this.displayNotice("emailAlreadyRegisteredInCognito");
      }
      this.setState({ isLoading: false });
      return null;
    }

    let { code, res } = await Service.signUp(email, passwordToCreate.trim());
    if (code === "UsernameExistsException") {
      const { complete } = await this.clearUnconfirmedUser(email);

      if (complete) {
        const { code: code1, res: res1 } = await Service.signUp(
          email,
          passwordToCreate.trim()
        );
        if (code1 === "error") {
          AppErrorActions.setSafeAppError(Error(res1));
          this.displayNotice("defaultSignUpError");
          this.setState({ isLoading: false });
          return null;
        }
        code = code1;
        res = res1;
      } else {
        this.setState({ isLoading: false });
        return null;
      }
    }

    switch (code) {
      case "UsernameExistsException":
        this.displayNotice("emailAlreadyRegistered");
        break;
      case "UserLambdaValidationException":
      case "InvalidParameterException":
        this.displayNotice("wrongInputData");
        break;
      case "error":
        this.displayNotice("defaultSignUpError");
        AppErrorActions.setSafeAppError(Error(res));
        break;
      case "ok":
      default:
        break;
    }
    this.setState({ isLoading: false });

    if (code === "ok") {
      onCognitoSignUpComplete({ email, password: passwordToCreate.trim() });
    }
  };

  displayNotice(type, callback) {
    const { NoticeModalActions } = this.props;
    let content;
    switch (type) {
      case "emailNotFilledOut":
        content = <TXT name="msg_enterEmail" />;
        break;
      case "wrongEmail":
        content = <TXT name="msg_enterValidEmail" />;
        break;
      case "pwNotFilledOut":
        content = <TXT name="msg_enterPassword" />;
        break;
      case "twoPwNotSame":
        content = <TXT name="msg_matchBothPassword" />;
        break;
      case "emailAlreadyRegisteredInCognito":
      case "emailAlreadyRegistered":
        content = <TXT name="msg_alreadyRegisteredUser" />;
        break;
      case "emailAlreadyRegisteredInGoogle":
        content = <TXT name="msg_registeredUserInGoogle" />;
        break;
      case "wrongInputData":
        content = <TXT name="msg_wrongEmailOrPassword" />;
        break;
      case "incorrectPwFormat":
        content = <TXT name="msg_pwFormatGuide" />;
        break;
      case "defaultSignUpError":
      default:
        content = <TXT name="msg_commonNetworkError" />;
        break;
    }
    NoticeModalActions.openModal({ content, callback });
  }

  render() {
    const { onModeToggleBtnClick } = this.props;
    const { isLoading } = this.state;

    return (
      <div>
        <CommonAccountPanelView
          title={<TXT name="titlePanel_signUp" />}
          textElement={[
            "email",
            "passwordToCreate",
            "confirmPassword",
            "signUpAgreement"
          ]}
          onInputValueChange={this.handleChange}
          firstBtnText={<TXT name="btnPanel_signUp" />}
          firstButtonDisabled={this.isSignUpBtnDisabled()}
          onFirstBtnClick={this.handleCognitoSignUp}
          modeToggleBtnText={<TXT name="btnPanelToggle_goLogIn" />}
          onModeToggleBtnClick={onModeToggleBtnClick}
        />
        <LoadingModalView open={isLoading} />
      </div>
    );
  }
}

export default withStore({
  dispatchMap: ["appError", "noticeModal"]
})(AccountRegisterPanel);
